/**
 * Shapes.js
 * 
 * This module provides functions for creating, manipulating, and offsetting geometric shapes
 * in the SightFlight Designer application. It handles operations such as:
 * 
 * 1. Creating rectangular and circular geometries on 3D planes
 * 2. Finding centers and radii of shapes
 * 3. Adding offset boundaries to obstacles (safety margins)
 * 4. Converting 2D screen coordinates to 3D world coordinates
 * 
 * These functions support obstacle creation and manipulation on roof segments.
 */

import * as THREE from "three";
import { POLYGONS, RENDER_LAYERS } from "../Global/variables";
import { createSegmentPlane } from "../Global/functions";
import { sceneManager } from "../SetUps/SceneManager";
import { MATERIALS } from "../Global/materials";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2";

/**
 * Creates a rectangular geometry on a 3D plane based on 2D screen coordinates
 * 
 * This function converts 2D screen coordinates (where the user has clicked and dragged)
 * into a 3D rectangle on the segment plane. It handles various cases of dragging direction.
 * 
 * @param {THREE.Vector2} startPoint - Start point in screen coordinates
 * @param {THREE.Vector2} endPoint - End point in screen coordinates
 * @param {THREE.Plane} facetPlane - The plane of the roof segment
 * @param {THREE.Raycaster} rayCaster - Raycaster for screen-to-world conversion
 * @param {THREE.Camera} camera - The camera for raycasting
 * @returns {Object} Object containing the points array and the geometry
 */
export function createRecGeo(startPoint, endPoint, facetPlane, rayCaster, camera) {
    // Avoid zero dimensions by adding a tiny value if needed
    if (startPoint.x === endPoint.x) {
        endPoint.x += Number.EPSILON;
    }

    if (startPoint.y === endPoint.y) {
        endPoint.y += Number.EPSILON;
    }

    // Calculate dimensions in screen space
    const left = Math.min(startPoint.x, endPoint.x);
    const right = Math.max(startPoint.x, endPoint.x);
    const top = Math.min(startPoint.y, endPoint.y);
    const bottom = Math.max(startPoint.y, endPoint.y);
    const width = right - left;
    const height = bottom - top;

    // Determine drag direction
    let dx = startPoint.x - endPoint.x < 0 ? -1 : 1;
    let dy = startPoint.y - endPoint.y < 0 ? -1 : 1;

    // Define the four corners of the rectangle in screen space
    // Default case: dragging from top-left to bottom-right or bottom-right to top-left
    let _vecTopLeft = new THREE.Vector3(startPoint.x, startPoint.y, 0);
    let _vecDownRight = new THREE.Vector3(endPoint.x, endPoint.y, 0);
    let _vecTopRight = new THREE.Vector3(
        startPoint.x - dx * width,
        startPoint.y,
        0
    );
    let _vecDownLeft = new THREE.Vector3(
        startPoint.x,
        startPoint.y - dy * height,
        0
    );

    // Handle alternate case: dragging from top-right to bottom-left or bottom-left to top-right
    if (dy === dx) {
        _vecTopLeft.set(startPoint.x, startPoint.y - dy * height, 0);
        _vecDownRight.set(startPoint.x - dx * width, startPoint.y, 0);
        _vecTopRight.set(endPoint.x, endPoint.y, 0);
        _vecDownLeft.set(startPoint.x, startPoint.y, 0);
    }

    // Vectors to store the 3D world coordinates of the rectangle corners
    let topleft = new THREE.Vector3();
    let topright = new THREE.Vector3();
    let bottomleft = new THREE.Vector3();
    let bottomright = new THREE.Vector3();

    // Convert screen coordinates to 3D points on the segment plane using raycasting
    rayCaster.setFromCamera(_vecTopLeft, camera);
    rayCaster.ray.intersectPlane(facetPlane, topleft);
    rayCaster.setFromCamera(_vecTopRight, camera);
    rayCaster.ray.intersectPlane(facetPlane, topright);
    rayCaster.setFromCamera(_vecDownLeft, camera);
    rayCaster.ray.intersectPlane(facetPlane, bottomleft);
    rayCaster.setFromCamera(_vecDownRight, camera);
    rayCaster.ray.intersectPlane(facetPlane, bottomright);
    
    // Create an array of points forming the rectangle (closing the loop)
    let points = [topleft, topright, bottomright, bottomleft, topleft];
    
    // Create a THREE.js buffer geometry from the points
    let g = new THREE.BufferGeometry().setFromPoints([
        topleft,
        topright,
        bottomright,
        bottomleft,
        topleft,
    ]);

    return { points, g };
}

/**
 * Creates a circular geometry on a 3D plane based on 2D screen coordinates
 * 
 * This function converts 2D screen coordinates (where the user has clicked and dragged)
 * into a 3D circle on the segment plane. The start point defines the center and the
 * distance to the end point defines the radius.
 * 
 * @param {THREE.Vector2} startPoint - Center point in screen coordinates
 * @param {THREE.Vector2} endPoint - Point on circumference in screen coordinates
 * @param {THREE.Plane} facetPlane - The plane of the roof segment
 * @param {THREE.Raycaster} rayCaster - Raycaster for screen-to-world conversion
 * @param {THREE.Camera} camera - The camera for raycasting
 * @returns {Object} Object containing the points array and the geometry
 */
export function createCirGeo(startPoint, endPoint, facetPlane, rayCaster, camera) {
    const points = [];
    
    // Center of the circle in screen space
    let centerX = startPoint.x;
    let centerY = startPoint.y;
    
    // Calculate the radius in screen space
    const radius = startPoint.distanceTo(endPoint);

    // Calculate delta from center to end point
    const deltaX = endPoint.x - centerX;
    const deltaY = endPoint.y - centerY;
    
    // Adjust the radius for better usability
    let dynamicRadius = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    dynamicRadius += radius;

    // Account for camera aspect ratio to ensure circles look circular
    const aspectRatio = camera.aspect;
    
    // Define number of segments for the circle (higher = smoother)
    let segments = 32;
    
    // Generate points around the circle
    for (let i = 0; i <= segments; i++) {
        const theta = (i / segments) * Math.PI * 2;
        
        // Adjust radius for aspect ratio
        const adjustedRadius = dynamicRadius / aspectRatio;
        
        // Calculate point in screen space
        const x = centerX + adjustedRadius * Math.cos(theta);
        const y = centerY + dynamicRadius * Math.sin(theta);

        // Create temporary vector for the point
        let temp = new THREE.Vector3(x, y, 0);

        // Convert screen coordinates to 3D point on the segment plane
        let newpoint = new THREE.Vector3();
        rayCaster.setFromCamera(temp, camera);
        rayCaster.ray.intersectPlane(facetPlane, newpoint);

        // Add the 3D point to the points array
        points.push(newpoint);
    }
    
    // Close the loop by adding the first point again
    points.push(points[0]);

    // Create a THREE.js buffer geometry from the points
    let geo = new THREE.BufferGeometry().setFromPoints(points);
    
    return { points, geo };
}

/**
 * Finds the center of a mesh using its bounding box
 * 
 * @param {THREE.Mesh} mesh - The mesh to find the center of
 * @returns {THREE.Vector3} The center point of the mesh
 */
export function findCenter(mesh) {
    // Create a bounding box from the mesh
    const bbox = new THREE.Box3().setFromObject(mesh);
    
    // Get the center of the bounding box
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    return center;
}

/**
 * Adds an offset boundary to an obstacle mesh
 * 
 * This function creates a visual boundary around obstacles to represent
 * safety margins that panels should not intersect with. It handles both
 * circular and rectangular obstacles differently.
 * 
 * @param {THREE.Mesh} mesh - The obstacle mesh to add offset to
 * @param {Array<THREE.Vector3>} points - Array of points defining the obstacle shape
 * @param {string} type - Type of obstacle ("circle" or "rectangle")
 * @param {THREE.Shape} shape - The 2D shape of the obstacle
 * @param {number} offset - Offset distance in inches
 * @param {number} height - Height of the obstacle
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @returns {Object} Object containing the updated mesh, shape, and points
 */
export function addOffset(mesh, points, type, shape, offset, height, segmentIndex, options = {}) {
    const requestedOffset = Math.max(0, Number(offset) || 0);
    const showOffset = options?.showOffset !== false;
    const sourceOutline = closeRing(
        points.slice(0, points.length > 1 ? -1 : points.length).map((p) => p.clone())
    );
    const rawOffsetOutline = getOffsetOutlinePoints(points, type, requestedOffset);
    const allowedSegments = getAllowedSegmentsForOffset(sourceOutline, segmentIndex);
    const allowedPolygons = allowedSegments.map((entry) => entry.polygon);
    const clampedOffsetOutline =
        allowedPolygons.length > 0
            ? clampOutlineToAllowedPolygons(rawOffsetOutline, allowedPolygons)
            : rawOffsetOutline.map((p) => p.clone());
    const roofAlignedOffsetOutline = projectOutlineToAllowedSegmentPlanes(
        clampedOffsetOutline,
        allowedSegments
    );
    const closedOutline = closeRing(roofAlignedOffsetOutline);

    const offsetLine = createOffsetLineForMesh(mesh, closedOutline);
    const connectors = createOffsetConnectorsForMesh(mesh, sourceOutline, closedOutline);
    offsetLine.visible = showOffset;
    if (connectors) connectors.visible = showOffset;
    mesh.add(offsetLine);
    if (connectors) mesh.add(connectors);
    mesh.userData = {
        ...(mesh.userData || {}),
        obstacleBaseOutlineWorld: sourceOutline.map((p) => p.clone()),
        obstacleOffsetOutlineWorld: closedOutline.map((p) => p.clone()),
    };
    return { mesh: mesh, shape: shape, points: points, appliedOffset: requestedOffset };
}

export function projectObstaclePointsToRoof(points, parentSegmentIndex) {
    const sourcePoints = Array.isArray(points) ? points : [];
    if (!sourcePoints.length) return [];

    const hasClosedRing =
        sourcePoints.length > 2 &&
        sourcePoints[0]?.distanceToSquared &&
        sourcePoints[sourcePoints.length - 1]?.distanceToSquared &&
        sourcePoints[0].distanceToSquared(sourcePoints[sourcePoints.length - 1]) < 1e-10;

    const base = sourcePoints
        .slice(0, hasClosedRing ? -1 : sourcePoints.length)
        .map((p) => p.clone());
    if (!base.length) return [];

    const sourceOutline = closeRing(base.map((p) => p.clone()));
    const allowedSegments = getAllowedSegmentsForOffset(sourceOutline, parentSegmentIndex);
    const projected = projectOutlineToAllowedSegmentPlanes(base, allowedSegments);

    if (hasClosedRing && projected.length) {
        projected.push(projected[0].clone());
    }
    return projected;
}

function getOffsetOutlinePoints(points, type, offsetInches) {
    const offsetMeters = Math.max(0, Number(offsetInches) || 0) * 0.0254;
    const basePoints = points.slice(0, points.length > 1 ? -1 : points.length).map(
        (p) => new THREE.Vector3(p.x, p.y, p.z)
    );
    if (!basePoints.length) return [];

    const center = new THREE.Vector3();
    basePoints.forEach((p) => center.add(p));
    center.multiplyScalar(1 / basePoints.length);

    if (type === "circle") {
        return basePoints.map((point) => {
            const dir = point.clone().sub(center);
            const baseRadius = Math.sqrt(dir.lengthSq());
            if (baseRadius < 1e-9) return point.clone();
            return center.clone().add(dir.normalize().multiplyScalar(baseRadius + offsetMeters));
        });
    }

    if (type === "rectangle" && basePoints.length >= 4) {
        const p1 = basePoints[0];
        const p2 = basePoints[1];
        const p4 = basePoints[3];
        const x = new THREE.Vector3().subVectors(p2, p1).normalize().multiplyScalar(offsetMeters);
        const y = new THREE.Vector3().subVectors(p1, p4).normalize().multiplyScalar(offsetMeters);

        return [
            basePoints[0].clone().add(x.clone().negate()).add(y.clone()),
            basePoints[1].clone().add(x.clone()).add(y.clone()),
            basePoints[2].clone().add(x.clone()).add(y.clone().negate()),
            basePoints[3].clone().add(x.clone().negate()).add(y.clone().negate()),
        ];
    }

    // Polygon fallback: radial expansion from centroid.
    return basePoints.map((point) => {
        const dir = point.clone().sub(center);
        if (dir.lengthSq() < 1e-10) return point.clone();
        return point.clone().add(dir.normalize().multiplyScalar(offsetMeters));
    });
}

function closeRing(points) {
    if (!Array.isArray(points) || points.length === 0) return [];
    const ring = points.map((p) => p.clone());
    if (ring[0].distanceToSquared(ring[ring.length - 1]) > 1e-10) {
        ring.push(ring[0].clone());
    }
    return ring;
}

function clampOutlineToSegment(outline, segmentPolygon) {
    if (!Array.isArray(outline) || outline.length === 0) return [];
    if (!Array.isArray(segmentPolygon) || segmentPolygon.length < 3) {
        return outline.map((p) => p.clone());
    }
    return outline.map((p) => clampPointToPolygonBoundary2D(p, segmentPolygon));
}

function clampOutlineToAllowedPolygons(outline, polygons) {
    if (!Array.isArray(outline) || outline.length === 0) return [];
    if (!Array.isArray(polygons) || polygons.length === 0) {
        return outline.map((p) => p.clone());
    }
    if (polygons.length === 1) {
        return clampOutlineToSegment(outline, polygons[0]);
    }
    return outline.map((p) => clampPointToAllowedPolygons2D(p, polygons));
}

function clampPointToPolygonBoundary2D(point, polygon) {
    const p2 = new THREE.Vector2(point.x, point.y);
    if (pointInPolygonOrOnEdge2D(p2, polygon)) {
        return point.clone();
    }

    let nearest = null;
    let nearestDistSq = Infinity;
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const candidate = nearestPointOnSegment2D(p2, a, b);
        const d2 = candidate.distanceToSquared(p2);
        if (d2 < nearestDistSq) {
            nearestDistSq = d2;
            nearest = candidate;
        }
    }

    if (!nearest) return point.clone();
    return new THREE.Vector3(nearest.x, nearest.y, point.z);
}

function clampPointToAllowedPolygons2D(point, polygons) {
    const p2 = new THREE.Vector2(point.x, point.y);
    for (const poly of polygons) {
        if (pointInPolygonOrOnEdge2D(p2, poly)) return point.clone();
    }

    let nearest = null;
    let nearestDistSq = Infinity;
    for (const poly of polygons) {
        for (let i = 0; i < poly.length; i++) {
            const a = poly[i];
            const b = poly[(i + 1) % poly.length];
            const candidate = nearestPointOnSegment2D(p2, a, b);
            const d2 = candidate.distanceToSquared(p2);
            if (d2 < nearestDistSq) {
                nearestDistSq = d2;
                nearest = candidate;
            }
        }
    }

    if (!nearest) return point.clone();
    return new THREE.Vector3(nearest.x, nearest.y, point.z);
}

function nearestPointOnSegment2D(p, a, b) {
    const ab = b.clone().sub(a);
    const denom = ab.lengthSq();
    if (denom < 1e-12) return a.clone();
    const t = THREE.MathUtils.clamp(p.clone().sub(a).dot(ab) / denom, 0, 1);
    return a.clone().add(ab.multiplyScalar(t));
}

function getSegmentPolygon2D(segmentIndex) {
    const segment = POLYGONS?.[segmentIndex];
    if (!segment || !Array.isArray(segment.edges) || segment.edges.length === 0) return null;

    const toV2 = (p) => new THREE.Vector2(p.x, p.y);
    const near = (a, b, eps = 1e-6) => a.distanceToSquared(b) <= eps * eps;

    const edges = segment.edges
        .filter((e) => e?.point1 && e?.point2)
        .map((e) => ({ a: toV2(e.point1), b: toV2(e.point2), used: false }));
    if (edges.length < 3) return null;

    const chain = [edges[0].a.clone(), edges[0].b.clone()];
    edges[0].used = true;

    while (true) {
        const tail = chain[chain.length - 1];
        let nextEdge = null;
        let nextPoint = null;

        for (const e of edges) {
            if (e.used) continue;
            if (near(e.a, tail)) {
                nextEdge = e;
                nextPoint = e.b;
                break;
            }
            if (near(e.b, tail)) {
                nextEdge = e;
                nextPoint = e.a;
                break;
            }
        }

        if (!nextEdge || !nextPoint) break;
        nextEdge.used = true;

        if (!near(nextPoint, chain[0])) {
            chain.push(nextPoint.clone());
        } else {
            break;
        }
    }

    if (chain.length >= 3) return chain;

    // Fallback if edges were not chainable.
    const pts = [];
    segment.edges.forEach((edge) => {
        if (edge?.point1) pts.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
        if (edge?.point2) pts.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });
    const unique = [];
    for (const p of pts) {
        if (!unique.some((u) => u.distanceToSquared(p) < 1e-12)) unique.push(p);
    }
    if (unique.length < 3) return null;
    const c = unique
        .reduce((acc, p) => acc.add(p), new THREE.Vector2())
        .multiplyScalar(1 / unique.length);
    unique.sort(
        (a, b) =>
            Math.atan2(a.y - c.y, a.x - c.x) - Math.atan2(b.y - c.y, b.x - c.x)
    );
    return unique;
}

function pointInPolygonOrOnEdge2D(point, polygon) {
    const edgeTolerance = 1e-5;
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        if (distancePointToSegment2D(point, a, b) <= edgeTolerance) return true;
    }

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x;
        const yi = polygon[i].y;
        const xj = polygon[j].x;
        const yj = polygon[j].y;
        const intersect =
            (yi > point.y) !== (yj > point.y) &&
            point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
}

function distancePointToSegment2D(p, a, b) {
    const ab = b.clone().sub(a);
    const ap = p.clone().sub(a);
    const denom = ab.lengthSq();
    if (denom < 1e-12) return p.distanceTo(a);
    const t = THREE.MathUtils.clamp(ap.dot(ab) / denom, 0, 1);
    const proj = a.clone().add(ab.multiplyScalar(t));
    return p.distanceTo(proj);
}

function getAllowedSegmentsForOffset(sourceOutline, parentSegmentIndex) {
    const obstacle2D = sourceOutline
        .slice(0, sourceOutline.length > 1 ? -1 : sourceOutline.length)
        .map((p) => new THREE.Vector2(p.x, p.y));
    if (obstacle2D.length < 3) return [];

    const overlaps = [];
    for (let i = 0; i < POLYGONS.length; i++) {
        const segPoly = getSegmentPolygon2D(i);
        if (!segPoly || segPoly.length < 3) continue;
        if (polygonsOverlap2D(segPoly, obstacle2D)) {
            overlaps.push({ index: i, polygon: segPoly, plane: createSegmentPlane(i) });
        }
    }

    const parentPoly = getSegmentPolygon2D(parentSegmentIndex);
    const parentPlane =
        typeof parentSegmentIndex === "number" ? createSegmentPlane(parentSegmentIndex) : null;
    const parentInOverlaps =
        typeof parentSegmentIndex === "number" &&
        overlaps.some((entry) => entry.index === parentSegmentIndex);

    if (!parentInOverlaps && parentPoly && parentPoly.length >= 3) {
        overlaps.unshift({ index: parentSegmentIndex, polygon: parentPoly, plane: parentPlane });
    }

    if (overlaps.length <= 1) {
        if (parentPoly && parentPoly.length >= 3) {
            return [{ index: parentSegmentIndex, polygon: parentPoly, plane: parentPlane }];
        }
        return overlaps;
    }

    return overlaps;
}

function projectOutlineToAllowedSegmentPlanes(outline, allowedSegments) {
    if (!Array.isArray(outline) || outline.length === 0) return [];
    if (!Array.isArray(allowedSegments) || allowedSegments.length === 0) {
        return outline.map((p) => p.clone());
    }
    return outline.map((point) => projectPointToBestAllowedSegmentPlane(point, allowedSegments));
}

function projectPointToBestAllowedSegmentPlane(point, allowedSegments) {
    if (!point) return point;
    const p2 = new THREE.Vector2(point.x, point.y);

    let selected = null;

    // Prefer the segment polygon that contains this XY.
    for (const entry of allowedSegments) {
        if (entry?.polygon && pointInPolygonOrOnEdge2D(p2, entry.polygon)) {
            selected = entry;
            break;
        }
    }

    // Otherwise use nearest allowed segment boundary in XY.
    if (!selected) {
        let nearestDistSq = Infinity;
        for (const entry of allowedSegments) {
            const poly = entry?.polygon;
            if (!Array.isArray(poly) || poly.length < 3) continue;
            for (let i = 0; i < poly.length; i++) {
                const a = poly[i];
                const b = poly[(i + 1) % poly.length];
                const candidate = nearestPointOnSegment2D(p2, a, b);
                const d2 = candidate.distanceToSquared(p2);
                if (d2 < nearestDistSq) {
                    nearestDistSq = d2;
                    selected = entry;
                }
            }
        }
    }

    if (!selected?.plane) return point.clone();
    return projectPointToPlaneKeepingXY(point, selected.plane);
}

function projectPointToPlaneKeepingXY(point, plane) {
    const normal = plane?.normal;
    if (!normal || Math.abs(normal.z) < 1e-8) return point.clone();
    const z = (-plane.constant - normal.x * point.x - normal.y * point.y) / normal.z;
    return new THREE.Vector3(point.x, point.y, z);
}

function polygonsOverlap2D(polyA, polyB) {
    if (!Array.isArray(polyA) || !Array.isArray(polyB) || polyA.length < 3 || polyB.length < 3) {
        return false;
    }

    if (polyA.some((p) => pointInPolygonOrOnEdge2D(p, polyB))) return true;
    if (polyB.some((p) => pointInPolygonOrOnEdge2D(p, polyA))) return true;

    for (let i = 0; i < polyA.length; i++) {
        const a1 = polyA[i];
        const a2 = polyA[(i + 1) % polyA.length];
        for (let j = 0; j < polyB.length; j++) {
            const b1 = polyB[j];
            const b2 = polyB[(j + 1) % polyB.length];
            if (segmentsIntersect2D(a1, a2, b1, b2)) return true;
        }
    }
    return false;
}

function segmentsIntersect2D(a1, a2, b1, b2) {
    const eps = 1e-9;
    const cross = (u, v) => u.x * v.y - u.y * v.x;
    const sub = (u, v) => new THREE.Vector2(u.x - v.x, u.y - v.y);

    const r = sub(a2, a1);
    const s = sub(b2, b1);
    const rxs = cross(r, s);
    const qpxr = cross(sub(b1, a1), r);

    if (Math.abs(rxs) < eps && Math.abs(qpxr) < eps) {
        const rLenSq = r.lengthSq();
        if (rLenSq < eps) return a1.distanceToSquared(b1) < eps;
        const t0 = sub(b1, a1).dot(r) / rLenSq;
        const t1 = sub(b2, a1).dot(r) / rLenSq;
        const tMin = Math.max(0, Math.min(t0, t1));
        const tMax = Math.min(1, Math.max(t0, t1));
        return tMax >= tMin - eps;
    }
    if (Math.abs(rxs) < eps) return false;

    const t = cross(sub(b1, a1), s) / rxs;
    const u = cross(sub(b1, a1), r) / rxs;
    return t >= -eps && t <= 1 + eps && u >= -eps && u <= 1 + eps;
}

function createOffsetLineForMesh(targetMesh, worldRingPoints) {
    const localPoints = worldRingPoints.map((p) => targetMesh.worldToLocal(p.clone()));
    if (localPoints.length < 2) return null;

    const positions = [];
    for (let i = 0; i < localPoints.length - 1; i++) {
        const a = localPoints[i];
        const b = localPoints[i + 1];
        positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }

    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);

    const rendererSize = new THREE.Vector2();
    sceneManager?.renderer?.getSize?.(rendererSize);
    const material = new LineMaterial({
        color: MATERIALS.setbackWireframe.color?.getHex?.() ?? 0xffffff,
        linewidth: 1.5,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 1,
    });
    material.resolution.set(
        rendererSize.x || window.innerWidth || 1,
        rendererSize.y || window.innerHeight || 1
    );

    const line = new LineSegments2(geometry, material);
    line.computeLineDistances?.();
    line.raycast = () => {};
    line.renderOrder = RENDER_LAYERS.OBSTACLE_OUTLINE;
    line.name = "offset";
    return line;
}

function createOffsetConnectorsForMesh(targetMesh, sourceWorldRing, offsetWorldRing) {
    if (!Array.isArray(sourceWorldRing) || !Array.isArray(offsetWorldRing)) return null;
    if (sourceWorldRing.length < 2 || offsetWorldRing.length < 2) return null;

    const source = closeRing(sourceWorldRing);
    const offset = closeRing(offsetWorldRing);
    const edgeCount = Math.min(source.length, offset.length) - 1;
    if (edgeCount <= 0) return null;

    const connectorSpacing = 0.1; // meters (~4 in) to match segment setback hatch density
    const thresholdSq = 1e-10;
    const connectorPoints = [];

    for (let i = 0; i < edgeCount; i++) {
        const s1 = source[i];
        const s2 = source[i + 1];
        const o1 = offset[i];
        const o2 = offset[i + 1];

        const sourceEdgeLen = s1.distanceTo(s2);
        const offsetEdgeLen = o1.distanceTo(o2);
        const samples = Math.max(1, Math.ceil(Math.max(sourceEdgeLen, offsetEdgeLen) / connectorSpacing));

        for (let j = 0; j <= samples; j++) {
            const t = j / samples;
            const from = s1.clone().lerp(s2, t);
            const to = o1.clone().lerp(o2, t);
            if (from.distanceToSquared(to) <= thresholdSq) continue;

            connectorPoints.push(targetMesh.worldToLocal(from.clone()));
            connectorPoints.push(targetMesh.worldToLocal(to.clone()));
        }
    }

    if (!connectorPoints.length) return null;

    const positions = [];
    for (let i = 0; i < connectorPoints.length; i += 2) {
        const a = connectorPoints[i];
        const b = connectorPoints[i + 1];
        if (!a || !b) continue;
        positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
    const connectorsGeometry = new LineSegmentsGeometry();
    connectorsGeometry.setPositions(positions);

    const rendererSize = new THREE.Vector2();
    sceneManager?.renderer?.getSize?.(rendererSize);
    const connectorsMaterial = new LineMaterial({
        color: MATERIALS.setbackWireframe.color?.getHex?.() ?? 0xffffff,
        linewidth: 1.5,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 1,
    });
    connectorsMaterial.resolution.set(
        rendererSize.x || window.innerWidth || 1,
        rendererSize.y || window.innerHeight || 1
    );

    const connectors = new LineSegments2(connectorsGeometry, connectorsMaterial);
    connectors.computeLineDistances?.();
    connectors.raycast = () => {};
    connectors.renderOrder = RENDER_LAYERS.OBSTACLE_OUTLINE;
    connectors.name = "offsetConnectors";
    return connectors;
}
